import { forwardRef, Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Utilities } from '@shared/utilities/utilities.model';
import { HttpAuthService } from '@shared/service/http-auth.service';
import { CommodityModel } from '@shared/models/commodity.model';
import { CrossPermissionModel, UserPermissionsModel } from '@shared/models/user-permission.model';
import { UserV3Service } from '@shared/service/user-v-3.service';
import { AppConfigEnvironmentService } from '@shared/service/appconfig-environment.service';
import { AppConfigEnvironment } from '@shared/models/appconfig-environment.model';
import { PermissionFilterModel } from '@shared/models/permission-filter.model';
import { ROOT_CONTRACT_CLOSING, ROOT_USER_CHANGE } from 'app/app-routing-roots';
import { catchError, map, tap } from 'rxjs/operators';
import { forkJoin, throwError } from 'rxjs';
import { UserV3Model } from '@shared/models/user-v-3.model';
import { Response } from '@angular/http';
import { PROFILE_CLAIMS_MYALD_KEY, PROFILE_PUBLIC_USER_KEY } from '../../permissions-variables';
import { Location } from '@angular/common';
import { BookingsRoutingRoots } from 'app/bookings/bookings-routing-roots';
import { NemoModuleKeys } from '@shared/models/module.model';
import { get_current_item_menu_id } from '../utilities/utilities-function';

const AVAILABLE_ALERT_MODULES: string[] = [NemoModuleKeys.TYRES, NemoModuleKeys.AUTHORITY, NemoModuleKeys.CONTRACT_CLOSING_];

@Injectable()
export class PermissionService {

	environment: AppConfigEnvironment;
	currentItemMenuID: string;
	currentProfileKey: string;
	commodities: CommodityModel[];
	private userPermission: UserPermissionsModel;
	private pageSize: number = 1000;

	constructor(
		@Inject(forwardRef(() => HttpAuthService)) public httpAuthService: HttpAuthService,
		public userV3Service: UserV3Service,
		private appConfig: AppConfigEnvironmentService,
		private location: Location
	) {
		this.environment = appConfig.Environment;
	}

	getCommodities(filter: PermissionFilterModel, getAll?: boolean): Observable<CommodityModel[]> {
		let url: string = this.environment.PERMISSION_API + '/commodities/lite';
		if (Utilities.isVoid(filter)) {
			filter = new PermissionFilterModel();
			// fixme se non viene passato alcun filtro viene limitata la chiamata ai primi pageSize elementi
			filter.page_size = this.pageSize;
			filter.page = 1;
		} else {
			if (getAll) {
				filter.page_size = -1;
			}
		}
		url += filter.getFilterUrl().replace(/\+/g, '%2B');
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			tap(response => {
				if (!response.ok) {
					throw response;
				}
			}),
			map(response => (response.json() as CommodityPaged).commodities.map(commodity => new CommodityModel(commodity)))
		);
	}

	getCommoditiesPaged(filter: PermissionFilterModel): Observable<CommodityPaged> {
		let url: string = this.environment.PERMISSION_API + '/commodities/lite';
		if (Utilities.isVoid(filter)) {
			filter = new PermissionFilterModel();
			// fixme se non viene passato alcun filtro viene limitata la chiamata ai primi pageSize elementi
			filter.page_size = this.pageSize;
			filter.page = 1;
		}
		url += filter.getFilterUrl().replace(/\+/g, '%2B');
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			tap(response => {
				if (!response.ok) {
					throw response;
				}
			}),
			map(response => {
				const page: CommodityPaged = response.json();
				page.commodities = page.commodities.map(commodity => new CommodityModel(commodity));
				return page;
			})
		);
	}

	getCommoditiesByIdList(filter: { ids?: string[]; ald_commodity_ids?: number[]; page?: number; page_size?: number }): Observable<CommodityModel[]> {
		let url: string = this.environment.PERMISSION_API + '/commodities/in';
		filter.page_size = -1;
		if(!Utilities.isEmpty(filter.ids)){
			const newFilter = {
				page: filter.page,
				page_size: filter.page_size
			};
			let body = {ids: filter.ids};
			url += Utilities.getQuery(newFilter).replace(/\+/g, '%2B');
			return this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT).pipe(
				tap(response => {
					if (!response.ok) {
						throw response;
					}
				}),
				map(response => (response.json() as CommodityPaged).commodities.map(commodity => new CommodityModel(commodity)))
			);
		}
		else{
			url += Utilities.getQuery(filter).replace(/\+/g, '%2B');
			return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
				tap(response => {
					if (!response.ok) {
						throw response;
					}
				}),
				map(response => (response.json() as CommodityPaged).commodities.map(commodity => new CommodityModel(commodity)))
			);
		}
		
	}

	getCommoditiesByIdListPaged(filter: { ids?: string[]; ald_commodity_ids?: number[]; page?: number; page_size?: number }): Observable<CommodityPaged> {
		let url: string = this.environment.PERMISSION_API + '/commodities/in';
		if(!Utilities.isEmpty(filter.ids)){
			const newFilter = {
				page: filter.page,
				page_size: filter.page_size
			};
			let body = {ids: filter.ids};
			url += Utilities.getQuery(newFilter).replace(/\+/g, '%2B');
			return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
				tap(response => {
					if (!response.ok) {
						throw response;
					}
				}),
				map(response => {
					const page: CommodityPaged = response.json();
					page.commodities = page.commodities.map(commodity => new CommodityModel(commodity));
					return page;
				})
			);
		}
		else{
			url += Utilities.getQuery(filter).replace(/\+/g, '%2B');
			return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
				tap(response => {
					if (!response.ok) {
						throw response;
					}
				}),
				map(response => {
					const page: CommodityPaged = response.json();
					page.commodities = page.commodities.map(commodity => new CommodityModel(commodity));
					return page;
				})
			);
		}
		
	}

	getCommodityList(filter?: PermissionFilterModel): Observable<CommodityModel[]> {
		let url: string = this.environment.PERMISSION_API + '/commodities/lite';
		if (Utilities.isVoid(filter)) {
			filter = new PermissionFilterModel();
		}
		filter.page_size = -1;
		url += filter.getFilterUrl().replace(/\+/g, '%2B');
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			tap(response => {
				if (!response.ok) {
					throw response;
				}
			}),
			map(response => response.json().commodities),
			// tslint:disable-next-line:no-unnecessary-callback-wrapper
			catchError(error => throwError(error))
		);
	}

	userModuleAssociation(userId: string, moduleKey: string): Observable<void> {
		const body: { id: string } = {
			id: moduleKey
		};
		const url: string =
			this.environment.PERMISSION_API + '/users/' + userId + '/modules';
		return new Observable<void>(observer => {
			this.httpAuthService
				.post(url, body, this.environment.ROOT_REDIRECT, null)
				.subscribe(
					response => {
						if (
							!Utilities.isVoid(response) &&
							response.status === 201
						) {
							observer.next();
						} else {
							observer.error('response: ' + response);
						}
					},
					error => {
						observer.error(error);
					}
				);
		});
	}

	userCrossAssociation(userId: string, moduleKey: string): Observable<void> {
		const body: { key: string, properties: any } = {
			key: moduleKey,
			properties: []
		};
		const url: string = this.environment.PERMISSION_API + '/users/' + userId + '/cross_permissions';
		return new Observable<void>(observer => {
			this.httpAuthService.post(url, body, this.environment.ROOT_REDIRECT, null)
				.subscribe(response => {
					if (!Utilities.isVoid(response) && response.status === 201) {
						observer.next();
					} else {
						observer.error('response: ' + response);
					}
				}, error => {
					observer.error(error);
				});
		});
	}

	userProfileAssociation(
		userId: string,
		profileKey: string,
		commoditiesId?: string[]
	): Observable<void> {
		const body: { id: string, commodities: string[] } = {
			id: profileKey,
			commodities: commoditiesId
		};
		const url: string =
			this.environment.PERMISSION_API +
			'/users/' +
			userId +
			'/profiles';
		return new Observable<void>(observer => {
			this.httpAuthService
				.post(url, body, this.environment.ROOT_REDIRECT, null)
				.subscribe(
					response => {
						if (
							!Utilities.isVoid(response) &&
							response.status === 201
						) {
							observer.next();
						} else {
							observer.error('response: ' + response);
						}
					},
					error => {
						observer.error(error);
					}
				);
		});
	}

	getUserV3Permission(): Observable<UserPermissionsModel> {
		if (!Utilities.isVoid(this.userPermission)) {
			return new Observable<UserPermissionsModel>(observer =>
				observer.next(this.userPermission)
			);
		}
		const currentUser: UserV3Model = this.userV3Service.getCurrentUser();
		if (!Utilities.isVoid(currentUser)) {
			return new Observable<UserPermissionsModel>(observer => {
				this.handlerUserPermissions(currentUser.id, observer);
			});
		}
		return new Observable<UserPermissionsModel>(observer => {
			this.userV3Service.getUser().subscribe(user => {
				this.handlerUserPermissions(user.id, observer);
			});
		});
	}


	refreshMultiProfileUserPermissions(model: any, profilyKey: any, itemMenuId:any) : Observable<UserPermissionsModel>{
		return new Observable<UserPermissionsModel>(observer => {
				this.userPermission = new UserPermissionsModel(model);
				this.userPermission.setCurrentProfile(profilyKey);
				this.currentProfileKey=profilyKey;
				this.currentItemMenuID=itemMenuId;
				this.setCurrentItemMenuId(itemMenuId);
				//this.getCurrentUserPermissions().setProfilePermission(profilyKey);
				observer.next(this.userPermission);
		});
	}

	refreshMultiProfileUserPermissionsSync(model: UserPermissionsModel, profilyKey: string) : void {		
		this.userPermission = new UserPermissionsModel(model);
		this.userPermission.setCurrentProfile(profilyKey);
	}


	getCurrentUserPermissions(): UserPermissionsModel {
		// lasciare questo mock per il refactoring negli altri component
		// userPermission: UserPermissionsModel;
		// this.userPermission = this.permissionService.getCurrentUserPermissions();
		return this.userPermission;
	}

	getUserModules(): string[] {
		if (!Utilities.isVoid(this.userPermission)) {
			return this.userPermission.profiles.map(m => m.module.key);
		}
	}

	private handlerUserPermissions(user_id: string, observer: any): any {
		this.httpAuthService.getUserPermission(user_id)
			.subscribe(response => {
				if (!Utilities.isVoid(response) && response.status === 200) {
					const model: any = response.json();
					if (!Utilities.isVoid(model)) {
						this.userPermission = new UserPermissionsModel(model);
						observer.next(this.userPermission);
					} else {
						observer.error('model: ' + model);
					}
				} else {
					observer.error('response: ' + response);
				}
			}, error => {
				observer.error(error);
			});
	}


	hasPermissions(permissions: string | string[]): boolean {
		return !Utilities.isVoid(this.userPermission) && this.userPermission.hasPermissions(permissions);
	}


	getCommoditiesService = (url: string, context?: string): Observable<CommodityModel[]> => {
			
		const getContext = (context: string): string => {

			if (Utilities.isVoid(context)) return ''

			let handler = {};
			handler[ROOT_CONTRACT_CLOSING] = (): string => '&flag_return_point=true&status=1';
			handler[ROOT_USER_CHANGE] = (): string => '&flag_delivery_point=true';

			return handler[context]();
		};

		if (!Utilities.isVoid(context))
			url += getContext(context);

		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			tap(response => {
				if (!response.ok) {
					throw response;
				}
			}),
			map(response => response.json().commodities.map(commodity => new CommodityModel(commodity)))
		);
	}

	getCommoditiesByAddress(lat: string, lng: string, radius: number, context?: string): Observable<CommodityModel[]> {
		
		if (Utilities.isVoid(lat) || Utilities.isVoid(lng) || Utilities.isVoid(radius)) return null;
		
		let url: string = `${this.environment.PERMISSION_API}/commodities/lite?lat=${lat}&lng=${lng}&radius=${radius}`;
		return this.getCommoditiesService(url, context);
	}

	getCommoditiesByName = (name: string, context?: string): Observable<CommodityModel[]> => {
		
		if (Utilities.isVoid(name)) return null;

		const url: string = `${this.environment.PERMISSION_API}/commodities/lite?name=${name}&page_size=-1`;
		return this.getCommoditiesService(url, context);
	}
	
	getCommoditiesByCode = (code: string, context?: string): Observable<CommodityModel[]> => {
		
		if (Utilities.isVoid(code)) return null;

		const url: string = `${this.environment.PERMISSION_API}/commodities/lite?ald_commodity_id=${code}&page_size=-1`;
		return this.getCommoditiesService(url, context);
	}

	getCrossPermission(crossPermissionKey: string): CrossPermissionModel {
		if (!Utilities.isVoid(crossPermissionKey)) {
			return this.userPermission.getCrossPermission(crossPermissionKey);
		}
		return null;
	}

	hasCrossPermission(crossPermissionKey: string): boolean {
		if (!Utilities.isVoid(crossPermissionKey)) {
			return !Utilities.isVoid(
				this.userPermission.getCrossPermission(crossPermissionKey)
			);
		}
		return false;
	}

	hasSearchPermission(): boolean {
		return this.userPermission.hasPermissionForSearch();
	}

	// TODO
	// cambiare nome alla funzione: 
	// al massimo le commodity si prendono dal Modulo, e non dal MenuID...
	// PS: sostituire "From" con "By"...
	getCommoditiesFromItemMenuID(itemMenuID: string): CommodityModel[] {

		// TODO
		// andrebbe riscritta utilizzando ModuleService.getCurrentModule()

		if (!Utilities.isVoid(itemMenuID)) {
			if (itemMenuID === UserV3Model.AUTHORITY_ITEM_MENU_ID) {
				return this.userPermission.getModuleCommodities(NemoModuleKeys.AUTHORITY);
			}
			if (itemMenuID === UserV3Model.MOBILITY_ITEM_MENU_ID) {
				return this.userPermission.getModuleCommodities(NemoModuleKeys.MOBILITY);
			}
			let commodities: CommodityModel[] = this.userPermission.getCommodities(itemMenuID);
			if (itemMenuID === UserV3Model.TYRES_ITEM_OPC_MENU_ID) {
				commodities = commodities.filter(cmm => !Utilities.isVoid(cmm.id));
			}
			commodities.forEach(comm => {
				comm.setIsTyres(itemMenuID === UserV3Model.TYRES_ITEM_OPC_MENU_ID ||
					itemMenuID === UserV3Model.TYRES_ITEM_OPA_MENU_ID ||
					itemMenuID === UserV3Model.TYRES_ITEM_OPF_MENU_ID ||
					itemMenuID === UserV3Model.TYRES_ITEM_OPS_MENU_ID);
				comm.setLabels();
			});
			if (itemMenuID === UserV3Model.LOGISTICS_ITEM_OPASR_MENU_ID) {
				commodities = commodities.filter(t => t.ald_commodity_id !== CommodityModel.allCentersID);
			}
			return commodities;
		}
		return null;
	}

	getProfileCommodities(profileKey: string): CommodityModel[] {
		if (!Utilities.isVoid(profileKey)) {
			return this.userPermission.getProfileCommodities(profileKey);
		}
		return [];
	}

	getUserListByProfileKey(profileKeyList: string[], isPageable?: boolean): any {
		const url: string = this.environment.PERMISSION_API + '/users' + Utilities.getQuery({
			profile_key: profileKeyList,
			pagination: isPageable
		});
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT, null);
	}

	getModulesForNotificationCenter(): string[] {
		const user_modules: string[] = [];
		this.userPermission.profiles.map(profile => profile.module.key).forEach(user_module => {
			if (AVAILABLE_ALERT_MODULES.some(available_module => available_module === user_module)) {
				user_modules.push(AVAILABLE_ALERT_MODULES.find(available_module => available_module === user_module));
			}
		});
		return user_modules;
	}

	setCurrentItemMenuId(itemMenuID: string): void {
		this.currentItemMenuID = itemMenuID;
	}

	getCurrentItemMenuId(): string {
		return this.currentItemMenuID;
	}

	setCurrentItemMenuProfileKey(currentProfileKey: string): void {
		this.currentProfileKey = currentProfileKey;
	}

	getCurrentItemMenuProfileKey(): string {
		return this.currentProfileKey;
	}

	getCommoditiesByAddressForSuperUser(moduleKey: string, lat: string, lng: string, radius: number): Observable<CommodityModel[]> {
		let url: string = `${this.environment.CREATE_USER_API}/commodities/lite?lat=${lat}&lng=${lng}`;
		if (!Utilities.isVoid(radius)) {
			url = url + '&radius=' + radius;
		}

		if (!!moduleKey) {
			url += this.getSearchTypeByModule(moduleKey);
		}

		this.commodities = [];
		return this.getCommoditiesSuperUser(url);
		// return forkJoin(urlArray.map(urlEntry => this.getCommoditiesSuperUser(urlEntry)))
		// 	.pipe(
		// 		map(resp => {
		// 			resp.forEach(commodities => this.commodities = this.commodities.concat(commodities));
		// 			return this.commodities;
		// 		})
		// 	);
	}

	getCommoditiesSuperUser(url?: string, filter?: { ald_commodity_id?: string; name?: string; municipality?: string; region?: string, page_size: number }, moduleKey?: NemoModuleKeys): Observable<CommodityModel[]> {
		if (!url) {
			url = `${this.environment.CREATE_USER_API}/commodities/lite`;
		}
		if (filter) {
			url += Utilities.getQuery(filter).replace(/\+/g, '%2B');
		}

		if (!!moduleKey) {
			url += this.getSearchTypeByModule(moduleKey);
		}

		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			tap(response => {
				if (!response.ok) {
					throw response;
				}
			}),
			map(response => (response.json() as CommodityPaged).commodities.map(commodity => new CommodityModel(commodity)))
		);
	}

	get profileType(): ProfileType | '' {
		const profile: UserPermissionsModel = this.userPermission;
		const module: string = this.location.path().split('/')[1];

		switch (module) {
			case NemoModuleKeys.AUTHORITY :
				switch (true) {
					case profile.isAldOperator(NemoModuleKeys.AUTHORITY):
						return ProfileType.ALD;
					case profile.isCenterOperator(NemoModuleKeys.AUTHORITY):
						return ProfileType.SP;
					case profile.isDealerOperator(NemoModuleKeys.AUTHORITY):
						return ProfileType.OP;
					default:
						return '';
				}

			case NemoModuleKeys.CLAIMS :
				switch (true) {
					case profile.hasProfile([PROFILE_PUBLIC_USER_KEY]):
						return ProfileType.PUBLIC;
					case profile.hasProfile([PROFILE_CLAIMS_MYALD_KEY]):
						return ProfileType.MY_ALD;
					default:
						return '';
				}

			case NemoModuleKeys.TYRES :
				switch (true) {
					case profile.isCenterOperator(NemoModuleKeys.TYRES):
						return ProfileType.SP;
					case profile.isCenterSuperUserOperator(NemoModuleKeys.TYRES):
						return ProfileType.SUPER_USER;
					default:
						return '';
				}
				
			case NemoModuleKeys.BOOKINGS:
				switch (true) {
					case this.location.path().includes(BookingsRoutingRoots.PRELEASE):
						return ProfileType.PRELEASE;
					case this.location.path().includes(BookingsRoutingRoots.RELIEF):
						return ProfileType.RELIEF;
					default:
						return '';
				}

			default:
				return '';
		}
	}

	getCurrentModuleKey(): NemoModuleKeys {
		const itemMenuID: string = this.getCurrentItemMenuId();
		switch (itemMenuID) {
			case UserV3Model.AUTHORITY_ITEM_MENU_ID:
				return NemoModuleKeys.AUTHORITY;
			case UserV3Model.CONTRACT_CLOSING_SP_OP_ITEM_MENU_ID:
				return NemoModuleKeys.CONTRACT_CLOSING;
			case UserV3Model.TYRES_ITEM_OPC_MENU_ID:
				return NemoModuleKeys.TYRES;
			case UserV3Model.LOGISTICS_ITEM_OPASR_MENU_ID:
				return NemoModuleKeys.LOGISTICS;
			case UserV3Model.FLOTTA_POOL_MENU_ID:
				return NemoModuleKeys.FLOTTA_POOL;
			case UserV3Model.USER_CHANGE_SP_MENU_ID:
				return NemoModuleKeys.USER_CHNAGE;
			case UserV3Model.INVOICING_ITEM_MENU_ID:
				return NemoModuleKeys.INVOICING;
			case UserV3Model.NOTIFICATION_CENTER_ITEM_MENU_ID:
				return NemoModuleKeys.NOTIFICATION_CENTER;
			case UserV3Model.BOOKINGS_RELIEF_MENU_ID:
				return NemoModuleKeys.BOOKINGS;
			case UserV3Model.DELIVERY_ITEM_MENU_ID:
				return NemoModuleKeys.DELIVERY;
			case UserV3Model.CARGOS_ITEM_MENU_ID:
				return NemoModuleKeys.CARGOS;
		}
	}

	/**
	 * Questo metodo è l'inverso di getCurrentModuleKey()
	 * 
	 * TODO
	 * togliere questa logica basata sugli ItemMenuID
	 * sostituirla con quella basata sui moduli - es. ModuleService.getCurrentModule()
	 *
	 * @returns {string}
	 **/ 
	getModuleKeyFromMenu(): string {
		return get_current_item_menu_id(this.location, this);
	}

	getSearchTypeByModule(moduleKey: string): string {
		switch (moduleKey) {
			case NemoModuleKeys.USER_CHNAGE:
			case NemoModuleKeys.DELIVERY:
				return '&flag_delivery_point=true';
			case NemoModuleKeys.CONTRACT_CLOSING:
				return '&flag_return_point=true&status=1';
			case NemoModuleKeys.TYRES:
				return '&type_ids=4';
			case NemoModuleKeys.AUTHORITY:
				const type_ids: number[] = [1, 2, 3, 15, 20];
				return `&type_ids=${type_ids.toString()}`;
			case NemoModuleKeys.FLOTTA_POOL:
				return '&type_ids=7';
			case NemoModuleKeys.CLAIMS:
				return '&type_ids=2';
			case NemoModuleKeys.LOGISTICS:
				return '&type_ids=9';
			default:
				return '';
		}
	}

	updateCurrentProfile(userId: string, profileId: string): Observable<UserPermissionsModel> {
		const body: any = {};
		const url: string =
			this.environment.PERMISSION_API + '/users/' + userId + '/profiles/' + profileId + "/current";
		return new Observable<UserPermissionsModel>(observer => {
			this.httpAuthService
				.put(url, body, this.environment.ROOT_REDIRECT, null)
				.subscribe(
					response => {
						if (
							!Utilities.isVoid(response) &&
							(response.status === 201 || response.status === 200) 
						) {
							observer.next(response.json() as UserPermissionsModel);
						} else {
							observer.error('response: ' + response);
						}
					},
					error => {
						observer.error(error);
					}
				);
		});
	}

}


enum ProfileType {
	SP = 'SP',
	OP = 'OP',
	ALD = 'ALD',
	PUBLIC = 'PUBLIC',
	MY_ALD = 'MY_ALD',
	SUPER_USER = 'SUPER_USER',
	PRELEASE = 'PRELEASE',
	RELIEF = 'RELIEF'
}


export interface CommodityPaged {
	stats: CommodityStats;
	commodities: CommodityModel[];
}

export interface CommodityStats {
	current_page: number;
	item_count: number;
	page_size: number;
	page_count: number;
}
